import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, tokenStore, setUnauthorizedHandler } from '../api/http.js';
import { connectSocket, disconnectSocket } from '../api/socket.js';
import { resumeAfterLogin } from '../api/queue.js';

const TERMINAL_KEY = 'mes-terminal';

/* Два пути входа: пароль для мастеров и руководства, терминал для операторов.
   Привязка терминала (код + токен устройства) хранится на устройстве и не
   зависит от того, кто на нём сейчас работает. */
export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const equipment = ref(null);
  const shift = ref(null);
  const shiftFetchedAt = ref(Date.now());
  const fullScope = ref(false);
  const loading = ref(false);
  const error = ref(null);
  const ready = ref(false);
  /* Почему сессия кончилась — показываем на экране входа и гасим при новом входе */
  const sessionEnded = ref('');

  const isAuthenticated = computed(() => !!user.value);
  const role = computed(() => user.value?.role || null);
  const isOperator = computed(() => role.value === 'operator');
  const canManageMasterData = computed(() => role.value === 'admin');
  const canWriteOrders = computed(() => ['supervisor', 'manager', 'admin'].includes(role.value));

  /* Идентификатор этого устройства.

     Заводится один раз и живёт рядом с привязкой терминала. Сервер закрепляет
     токен терминала за первым устройством, которое его предъявило, — поэтому
     утёкшая строка токена сама по себе перестаёт что-либо давать: с чужого
     планшета её не примут.

     Хранится отдельным ключом, а не внутри привязки: очистка привязки при
     перенастройке не должна менять устройство — оно физически то же самое. */
  const DEVICE_KEY = 'mes-device';
  function deviceId() {
    try {
      let id = localStorage.getItem(DEVICE_KEY);
      if (!id) {
        id = (crypto.randomUUID?.() || 'd-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12));
        localStorage.setItem(DEVICE_KEY, id);
      }
      return id;
    } catch {
      /* Приватный режим: идентификатор не переживёт перезагрузку, и терминал
         придётся настраивать заново. Для цехового планшета это не сценарий,
         но падать здесь нельзя. */
      return 'no-storage';
    }
  }

  /* Привязка устройства к терминалу: заводится один раз администратором
     при настройке планшета и переживает выход оператора. */
  const terminal = ref(readTerminal());
  function readTerminal() {
    try { return JSON.parse(localStorage.getItem(TERMINAL_KEY) || 'null'); } catch { return null; }
  }
  function saveTerminal(v) {
    terminal.value = v;
    try {
      if (v) localStorage.setItem(TERMINAL_KEY, JSON.stringify(v));
      else localStorage.removeItem(TERMINAL_KEY);
    } catch { /* приватный режим */ }
  }

  function applySession(data) {
    sessionEnded.value = '';
    user.value = data.user || null;
    equipment.value = data.equipment || null;
    shift.value = data.shift || null;
    /* Момент получения смены — точка отсчёта для поправки на часы планшета.
       Сервер прислал своё время, мы запомнили своё; разница дальше держит
       остаток смены честным даже на устройстве с уехавшими часами. */
    shiftFetchedAt.value = Date.now();
    fullScope.value = data.fullScope ?? !data.user?.scope?.length;
    if (data.token) tokenStore.set(data.token);
    if (user.value) {
      connectSocket();
      // Неотправленное с прошлой сессии должно уйти само, а не по кнопке
      resumeAfterLogin();
    }
  }

  /* Пересменок. Планшет у станка не перезагружают сутками, и без обновления
     он до утра показывал бы смену, которая кончилась в шесть вечера, —
     а вместе с ней неверную привязку всего, что человек видит на экране. */
  async function refreshShift() {
    if (!user.value) return;
    try { shift.value = (await api.get('/auth/me')).shift; shiftFetchedAt.value = Date.now(); }
    catch { /* связь пропала — покажем прежнюю смену, это лучше пустоты */ }
  }

  async function login(loginName, password) {
    loading.value = true; error.value = null;
    try {
      applySession(await api.post('/auth/login', { login: loginName, password }));
      return true;
    } catch (e) {
      error.value = e.message;
      return false;
    } finally { loading.value = false; }
  }

  /* Почему последняя попытка не удалась: сеть или отказ сервера.

     Разница принципиальная. Сервер отверг терминал — планшет действительно
     надо настраивать заново. Сети нет — привязка цела, и требовать у оператора
     токен устройства нельзя: токена у него нет, а планшет превращается
     в кирпич посреди смены. */
  const lastCallOffline = ref(false);

  /* Терминал представляется устройством и получает список своих станков —
     до ввода табельного номера. Пользовательский токен здесь ещё не выдаётся. */
  async function terminalSession(terminalCode, deviceToken) {
    loading.value = true; error.value = null; lastCallOffline.value = false;
    try {
      const data = await api.post('/auth/terminal/session', { terminalCode, deviceToken, deviceId: deviceId() });
      saveTerminal({ code: terminalCode, deviceToken, ...data.terminal });
      shift.value = data.shift;
      return data;
    } catch (e) {
      error.value = e.message;
      lastCallOffline.value = e.offline === true;
      return null;
    } finally { loading.value = false; }
  }

  async function terminalLogin(tabNumber, pin, machineId = null) {
    if (!terminal.value) { error.value = 'Терминал не настроен'; return false; }
    loading.value = true; error.value = null;
    try {
      applySession(await api.post('/auth/terminal/login', {
        terminalCode: terminal.value.code,
        deviceToken: terminal.value.deviceToken,
        deviceId: deviceId(),
        tabNumber, pin,
        machineId: machineId || undefined,
      }));
      return true;
    } catch (e) {
      error.value = e.message;
      return false;
    } finally { loading.value = false; }
  }

  const currentToken = () => { try { return tokenStore.get(); } catch { return null; } };
  let seenToken = currentToken();

  /* Восстановление сессии при перезагрузке страницы. Планшет в цехе
     перезагружают, и повторный ввод PIN каждый раз — это потеря времени. */
  async function restore() {
    if (!tokenStore.get()) { ready.value = true; return; }
    try {
      applySession(await api.get('/auth/me'));
      seenToken = currentToken();
    } catch {
      tokenStore.set(null);
      user.value = null;
    } finally { ready.value = true; }
  }

  function logout() {
    tokenStore.set(null);
    disconnectSocket();
    user.value = null;
    equipment.value = null;
    fullScope.value = false;
    // Привязку терминала НЕ трогаем: устройство остаётся настроенным
  }

  /* Вход в соседней вкладке.

     Вкладки одного браузера делят localStorage, поэтому токен там один на всех:
     вошли планировщиком во второй вкладке — первая продолжает показывать
     мастера, но её запросы уже уходят от планировщика. Права при этом не текут,
     сервер отказывает честно, — но человек видит своё имя и не понимает,
     почему ему нельзя. Хуже всего то, что экран врёт молча.

     Событие storage приходит только в ДРУГИЕ вкладки, поэтому своей же
     записи мы здесь не увидим и цикла не будет. */
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== 'mes-token') return;   // null = очистили всё хранилище
    const now = currentToken();
    if (now === seenToken) return;
    seenToken = now;

    if (!now) {
      // В соседней вкладке вышли — выходим и здесь, App.vue уведёт на вход
      disconnectSocket();
      user.value = null;
      equipment.value = null;
      return;
    }
    // Вошёл кто-то другой: перечитываем, кто мы теперь, вместо показа прежнего
    void restore();
  });

  /* Истёкший или отозванный токен.

     Здесь только гасим сессию. Уводит на экран входа App.vue — он видит
     маршрутизатор, а хранилище нет: маршрутизатор сам обращается к этому
     хранилищу, и импорт в обратную сторону замкнул бы круг.

     Причину запоминаем: «Сессия истекла» и «Учётная запись недоступна»
     требуют разных действий, а выход без объяснения человек воспринимает
     как сбой и жмёт кнопки заново. */
  setUnauthorizedHandler((reason) => {
    if (user.value) sessionEnded.value = reason || 'Сессия завершена';
    tokenStore.set(null);
    disconnectSocket();
    user.value = null;
    equipment.value = null;
  });

  return {
    user, equipment, shift, shiftFetchedAt, fullScope, loading, error, ready, terminal, sessionEnded,
    lastCallOffline,
    isAuthenticated, role, isOperator, canManageMasterData, canWriteOrders,
    login, terminalSession, terminalLogin, restore, logout, saveTerminal, refreshShift,
  };
});
